import {
  MostQuerySuggestedTask,
  MostRequestHistory,
  MostRunCommandType,
} from 'src/graphql/generated/graphql';
import { MostScheduledJob } from '../list/scheduled-jobs/form/types';
import { Command } from 'src/hooks/query/use-run-command';

type GetterProps = {
  storeId: string;
  task: Partial<MostQuerySuggestedTask>;
};

export const getValuesFromRequestHistory = (
  item: MostRequestHistory,
): Partial<MostScheduledJob> | undefined => {
  if (!item.queryId && !item.triageId) {
    return undefined;
  }

  if (!item.queryName && !item.triageName) {
    return undefined;
  }

  return {
    jobConfig: {
      command: {
        commandId: item.queryId || item.triageId || '',
        name: item.queryName || item.triageName || '',
        type: item.queryId
          ? MostRunCommandType.Query
          : MostRunCommandType.Triage,
      },
      devices: [item.device],
      storeIds: [item.store?.id as string],
    },
  };
};

export const getValuesFromTask = ({
  task,
  storeId,
}: GetterProps): Partial<MostScheduledJob> | undefined => {
  if (!task?.problemDefinition) {
    return undefined;
  }

  if (!task.problemDefinition?.triageId) {
    return {
      jobConfig: {
        command: {
          // both the query and the triage cannot be null for a suggested task
          commandId: task?.problemDefinition?.queryId as string,
          name: task?.problemDefinition?.name as string,
          type: MostRunCommandType.Query,
          description: task?.problemDefinition?.description as string,
        },
        devices: task?.computersWithProblem as string[],
        storeIds: [storeId],
      },
    };
  }

  return {
    jobConfig: {
      command: {
        // if the triage exists, execute that, not the query
        commandId: task?.problemDefinition?.triageId as string,
        name: task?.problemDefinition?.name as string,
        type: MostRunCommandType.Triage,
        description: task?.problemDefinition?.description as string,
      },
      devices: task?.computersWithProblem as string[],
      storeIds: [storeId],
    },
  };
};

export const getQueryFromTask = ({ task, storeId }: GetterProps): Command => {
  return {
    device: task.computersWithProblem as string[],
    nationalStoreNumber: storeId,
    queryId: task.problemDefinition?.queryId,
    name: task.problemDefinition?.name,
  };
};

export const getTriageFromTask = ({ task, storeId }: GetterProps): Command => {
  return {
    device: task.computersWithProblem as string[],
    nationalStoreNumber: storeId,
    triageId: task.problemDefinition?.triageId,
    name: task.problemDefinition?.name,
  };
};

/**
 * Returns a generated command from the task and store id inputs.
 *
 * It will ALWAYS prefer a triage over a query, so be careful when passing mixed data.
 *
 * If one or the other is preferred, defer to the other functions like getQueryFromTask, or getTraigeFromTask.
 * @param param0 The properties to generate the command from. Consists of a valid Task and National Store Number.
 * @returns a runnable Command object.
 */
export const getCommandFromTask = ({ task, storeId }: GetterProps): Command => {
  if (task.problemDefinition?.triageId) {
    return getTriageFromTask({ task, storeId });
  }

  return getQueryFromTask({ task, storeId });
};

const commandsWithConfirmation = [
  'Install SCCM Client',
  'New - Install DCEndpoint packages',
  'Reboot ALL Devices',
  'Reboot ALL POS',
  'Reboot EOTF Kiosk',
  'Reboot Individual Device',
  'Reinstall NP# on register or WST',
  'Reinstall NP# on register, WST or KVS - TEST',
  'Remote Virtual Machine Creation 18 V3 on RHS',
  'Rollback POS Business Date',
  // NOT IN THE LIST ADDED FOR TESTING PURPOSES ONLY
  'Printer Diagnostics - Install Printer',
];

const commmandIdsWithConfirmation = [''];

export const isWarningRequired = (
  name?: string | null,
  commandId?: string | null,
): boolean => {
  if (!name && !commandId) {
    return false;
  }

  if (
    name &&
    commandsWithConfirmation.some((value) =>
      value.localeCompare(name, undefined, {
        sensitivity: 'accent',
      }),
    )
  ) {
    return true;
  }

  if (
    commandId &&
    commmandIdsWithConfirmation.some((value) =>
      value.localeCompare(commandId, undefined, {
        sensitivity: 'accent',
      }),
    )
  ) {
    return true;
  }

  return false;
};
